import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/Button';
import { Input } from '../../../../components/forms/Input';
import { Select } from '../../../../components/forms/Select';
import {
  AvailabilityOwner,
  useAvailabilityExceptionMutations,
  useAvailabilityExceptions,
} from '../hooks';
import { ExceptionType, SchedulingApiError } from '../types';
import { fromApiTime, toApiTime } from '../utils';

interface ExceptionsEditorProps {
  owner: AvailabilityOwner;
  ownerId: string;
}

export const ExceptionsEditor: React.FC<ExceptionsEditorProps> = ({ owner, ownerId }) => {
  const { t, i18n } = useTranslation();
  const { data: exceptions = [] } = useAvailabilityExceptions(owner, ownerId);
  const mutations = useAvailabilityExceptionMutations(owner, ownerId);

  const [date, setDate] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [type, setType] = useState<ExceptionType>('closed');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = exceptions
    .filter(item => (item.date_end ?? item.date) >= today)
    .sort((a, b) => a.date.localeCompare(b.date));

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
      new Date(`${value}T00:00:00`)
    );

  const handleAdd = async () => {
    setError(null);
    try {
      await mutations.create.mutateAsync({
        date,
        date_end: dateEnd || null,
        type,
        start_time: toApiTime(start),
        end_time: toApiTime(end),
        reason: reason.trim(),
      });
      setDate('');
      setDateEnd('');
      setStart('');
      setEnd('');
      setReason('');
    } catch (err) {
      setError(
        err instanceof SchedulingApiError ? err.message : t('schedulingAdmin.errors.generic')
      );
    }
  };

  const typeOptions = [
    { value: 'closed', label: t('schedulingAdmin.exceptions.closed') },
    { value: 'custom_hours', label: t('schedulingAdmin.exceptions.customHours') },
  ];

  return (
    <div className="space-y-4">
      {upcoming.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('schedulingAdmin.exceptions.empty')}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {upcoming.map(item => (
            <li key={item.id} className="flex items-center justify-between gap-3 py-2">
              <div className="text-sm">
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {formatDate(item.date)}
                  {item.date_end &&
                    item.date_end !== item.date &&
                    ` – ${formatDate(item.date_end)}`}
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  {' · '}
                  {item.type === 'closed'
                    ? item.start_time
                      ? t('schedulingAdmin.exceptions.closedRange', {
                          start: fromApiTime(item.start_time),
                          end: fromApiTime(item.end_time),
                        })
                      : t('schedulingAdmin.exceptions.closedAllDay')
                    : t('schedulingAdmin.exceptions.hoursRange', {
                        start: fromApiTime(item.start_time),
                        end: fromApiTime(item.end_time),
                      })}
                  {item.reason && ` · ${item.reason}`}
                </span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                title={t('common.remove')}
                onClick={() => mutations.remove.mutate(item.id)}
              >
                <span className="material-icons text-base">delete</span>
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {t('schedulingAdmin.exceptions.add')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            type="date"
            label={t('schedulingAdmin.exceptions.date')}
            value={date}
            onChange={e => setDate(e.target.value)}
            fullWidth
          />
          <Input
            type="date"
            label={t('schedulingAdmin.exceptions.dateEnd')}
            value={dateEnd}
            min={date || undefined}
            onChange={e => setDateEnd(e.target.value)}
            fullWidth
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('schedulingAdmin.exceptions.type')}
          </label>
          <Select
            options={typeOptions}
            value={type}
            onChange={value => setType(value as ExceptionType)}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            type="time"
            label={t('schedulingAdmin.hours.from')}
            value={start}
            onChange={e => setStart(e.target.value)}
            helperText={
              type === 'closed' ? t('schedulingAdmin.exceptions.timesOptional') : undefined
            }
            fullWidth
          />
          <Input
            type="time"
            label={t('schedulingAdmin.hours.to')}
            value={end}
            onChange={e => setEnd(e.target.value)}
            fullWidth
          />
        </div>
        <Input
          label={t('schedulingAdmin.exceptions.reason')}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={t('schedulingAdmin.exceptions.reasonPlaceholder')}
          fullWidth
        />
        {error && <p className="text-sm text-red-600">{error}</p>}
        <Button onClick={handleAdd} loading={mutations.create.isPending} disabled={!date}>
          {t('common.add')}
        </Button>
      </div>
    </div>
  );
};
